import { useCallback, useState } from "react";
import { vceFirestore, hvoFirestore } from "../../configs/FirebaseConfig";
import { FIRESTORE_ORDERS_TABLE } from "../../constants/FirestoreConstant";
import { buildOrderSearch, collectSearchResults } from "../../functions/orderSearch";

const SHOPS = [
  { shopSource: "VCE", shopName: "Vleescentrum", firestore: () => vceFirestore },
  { shopSource: "HVO", shopName: "Halalvlees Online", firestore: () => hvoFirestore },
];

/** A code point above every normal character: `value` up to `value + PREFIX_END` is everything starting with it. */
const PREFIX_END = "\uf8ff";

const runQuery = (firestore, { field, op, value, limit }) => {
  let query = firestore.collection(FIRESTORE_ORDERS_TABLE);
  if (op === "prefix") query = query.where(field, ">=", value).where(field, "<=", `${value}${PREFIX_END}`);
  else query = query.where(field, op, value);
  return query.limit(limit).get();
};

/**
 * Runs every query of one shop. A query that fails is left out instead of failing the search; the
 * shop only counts as failed when none of its queries came back (not logged in on HVO, offline).
 */
const searchShop = async (shop, queries) => {
  const settled = await Promise.allSettled(queries.map((query) => runQuery(shop.firestore(), query)));
  const done = [];
  settled.forEach((item, index) => {
    if (item.status === "fulfilled") done.push({ snapshot: item.value, query: queries[index] });
    else console.error(`[useOrderSearch] ❌ ${shop.shopSource} query failed:`, item.reason);
  });

  const orders = done.flatMap(({ snapshot }) =>
    snapshot.docs
      // VCE orders in the HVO project are left out, as in useOrders
      .filter((doc) => shop.shopSource !== "HVO" || !doc.data().isVleescentrum)
      .map((doc) => ({ id: doc.id, ...doc.data(), shopSource: shop.shopSource, shopName: shop.shopName })),
  );
  return {
    orders,
    hitLimit: done.some(({ snapshot, query }) => snapshot.size >= query.limit),
    failed: queries.length > 0 && done.length === 0,
  };
};

/**
 * The order search of the orders page (HVC-90): every order of both shops, by order number, email,
 * name, company, phone or postcode. Which queries a search runs is decided in functions/orderSearch.js.
 *
 * `search` resolves to `{ kind, searchable, orders, total, incomplete, failedShops }`, `searchable`
 * false when the input gave nothing to search on. Orders are tagged with `shopSource` and `shopName`
 * like useOrders' orders, so the order drawer takes them as they are.
 */
export const useOrderSearch = () => {
  const [searching, setSearching] = useState(false);

  const search = useCallback(async (input) => {
    const plan = buildOrderSearch(input);
    if (plan.queries.length === 0) {
      return { kind: plan.kind, searchable: false, orders: [], total: 0, incomplete: false, failedShops: [] };
    }

    setSearching(true);
    try {
      const perShop = await Promise.all(SHOPS.map((shop) => searchShop(shop, plan.queries)));
      const { orders, total, incomplete } = collectSearchResults(
        perShop.flatMap((result) => result.orders),
        plan,
        perShop.some((result) => result.hitLimit),
      );
      const failedShops = SHOPS.filter((_, index) => perShop[index].failed).map((shop) => shop.shopSource);
      return { kind: plan.kind, searchable: true, orders, total, incomplete, failedShops };
    } finally {
      setSearching(false);
    }
  }, []);

  return { search, searching };
};
